import Vue from 'vue';
import {AxiosInstance} from 'axios';
import store from '@/store';

/**
 * Silent requests for endpoints that only exist on newer backends (Notify v2).
 *
 * They bypass `API.handleAxiosError`, so an old backend (404 / 400 / 405) never opens the error or unauthorized dialog;
 * the caller falls back to the old behaviour. Results of capability probes are cached per page view.
 */

export type SilentResult<T> =
    | { ok: true; data: T }
    | { ok: false; status?: number; message?: string; /** the error body was a JSON object (NestJS), not HTML/text of a proxy */ json?: boolean };

type Method = 'get' | 'put' | 'post' | 'delete';

const errorMessage = (data: unknown): string | undefined => {
    if (data && typeof data === 'object' && 'message' in data) {
        const message = (data as { message?: unknown }).message;
        return Array.isArray(message) ? message.join(', ') : String(message);
    }
    return typeof data === 'string' ? data : undefined;
};

export async function silentRequest<T>(method: Method, service: string, path: string, payload?: unknown): Promise<SilentResult<T>> {
    const axios = store.state.axios as AxiosInstance | null;
    if (!axios) {
        return {ok: false};
    }
    const url = `${service}/${path}`;
    try {
        let response;
        if (method === 'get' || method === 'delete') {
            response = await axios[method](url, payload ? {params: {json: JSON.stringify(payload)}} : undefined);
        } else {
            response = await axios[method](url, payload ?? {});
        }
        return {ok: true, data: response.data as T};
    } catch (e) {
        const response = (e as { response?: { status?: number; data?: unknown } }).response;
        const data = response?.data;
        return {ok: false, status: response?.status, message: errorMessage(data), json: !!data && typeof data === 'object'};
    }
}

/**
 * NestJS answers an unknown route with 404 `{"message": "Cannot PUT /path"}`. Only a JSON 404 with another message
 * comes from an existing route (e.g. "not found" of the entity); anything else counts as unknown (safe default).
 */
export const isUnknownRoute = (result: SilentResult<unknown>): boolean => {
    if (result.ok) {
        return false;
    }
    if (result.status === 405) {
        return true;
    }
    return result.status === 404 && (!result.json || !result.message || /^Cannot (GET|PUT|POST|DELETE|PATCH) /.test(result.message));
};

/** Capabilities of the connected backends; undefined = not probed yet */
export const backendFeatures = Vue.observable({
    /** notifier: counts, mark-unread, revoke-dismissions, snooze, dismiss-group, own filters */
    notifierV2: undefined as boolean | undefined,
    /** user-login-handler: me/preferences */
    preferences: undefined as boolean | undefined,
    /** to-do-manager: soft delete + items/restore/:id */
    toDoRestore: undefined as boolean | undefined,
});

const pending: Partial<Record<keyof typeof backendFeatures, Promise<boolean>>> = {};

/** Runs the probe once and caches the result */
export function probeFeature(key: keyof typeof backendFeatures, probe: () => Promise<boolean>): Promise<boolean> {
    const known = backendFeatures[key];
    if (known !== undefined) {
        return Promise.resolve(known);
    }
    if (!pending[key]) {
        pending[key] = probe()
            .catch(() => false)
            .then((value) => {
                backendFeatures[key] = value;
                delete pending[key];
                return value;
            });
    }
    return pending[key] as Promise<boolean>;
}

/** A probed feature turned out to be missing (e.g. the backend was rolled back) */
export function markFeatureMissing(key: keyof typeof backendFeatures): void {
    backendFeatures[key] = false;
}

/** A valid ObjectId that never exists, used to probe routes without touching data */
export const PROBE_OBJECT_ID = '000000000000000000000000';
